import { useState, useMemo, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Card,
  Typography,
  TextField,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  InputAdornment,
  alpha,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import SearchIcon from '@mui/icons-material/Search';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import HistoryIcon from '@mui/icons-material/History';
import axiosInstance from '../../lib/axios';
import { TableToolbar, FilterOption, ColumnConfig } from '../../components/TableToolbar';
import { getUser } from '../../utils/auth';
import type { InventoryMovement, InventoryMovementType } from '../../types';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { palette, typography, theme } from '../../theme/colors';
import { useAnimationStyles } from '../../hooks/useAnimations';

const fetchMovements = async (
  page: number,
  pageSize: number,
  filters: {
    movementType?: string;
    startDate?: string;
    endDate?: string;
  },
) => {
  const params: any = {
    page: page + 1,
    limit: pageSize,
  };

  if (filters.movementType) {
    params.movementType = filters.movementType;
  }
  if (filters.startDate) {
    params.startDate = filters.startDate;
  }
  if (filters.endDate) {
    params.endDate = filters.endDate;
  }

  const response = await axiosInstance.get('/inventory/organization/movements', { params });
  return {
    data: response.data.movements || [],
    total: response.data.total || 0,
  };
};

const movementTypeLabels: Record<InventoryMovementType, string> = {
  sale: 'SALE',
  sale_return: 'SALE RETURN',
  reservation: 'RESERVATION',
  reservation_cancel: 'RSV CANCEL',
  reservation_complete: 'RSV COMPLETE',
  transfer_out: 'TRANSFER OUT',
  transfer_in: 'TRANSFER IN',
  transfer_allocate: 'ALLOCATE',
  transfer_deallocate: 'DEALLOCATE',
  adjustment_add: 'ADD',
  adjustment_remove: 'REMOVE',
  restock: 'RESTOCK',
  loss: 'LOSS',
  count: 'COUNT',
  initial: 'INITIAL',
  delete: 'DELETED',
};

const getMovementTypeColor = (type: InventoryMovementType): string => {
  const positiveTypes: InventoryMovementType[] = ['sale_return', 'restock', 'transfer_in', 'adjustment_add', 'initial'];
  const negativeTypes: InventoryMovementType[] = ['sale', 'transfer_out', 'adjustment_remove', 'loss', 'reservation_complete', 'delete'];

  if (positiveTypes.includes(type)) return palette.success;
  if (negativeTypes.includes(type)) return palette.error;
  return palette.warning;
};

const filterOptions: FilterOption[] = [
  {
    field: 'movementType',
    label: 'Movement Type',
    type: 'select',
    options: Object.entries(movementTypeLabels).map(([value, label]) => ({ value, label })),
  },
];

const exportColumns: ColumnConfig[] = [
  {
    field: 'productName',
    headerName: 'Product',
    valueFormatter: (_, row) => row.product?.name || 'Unknown',
  },
  {
    field: 'productSku',
    headerName: 'SKU',
    valueFormatter: (_, row) => row.product?.sku || '-',
  },
  {
    field: 'movementType',
    headerName: 'Type',
    valueFormatter: (value) => movementTypeLabels[value as InventoryMovementType] || value,
  },
  { field: 'quantityChange', headerName: 'Change' },
  { field: 'quantityBefore', headerName: 'Before' },
  { field: 'quantityAfter', headerName: 'After' },
  { field: 'reason', headerName: 'Reason' },
  {
    field: 'createdAt',
    headerName: 'Date',
    valueFormatter: (value) => new Date(value).toLocaleString(),
  },
];

export const InventoryMovements = () => {
  const user = getUser();
  const { mode } = useContext(ColorModeContext);
  const animations = useAnimationStyles();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [searchValue, setSearchValue] = useState('');
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: '',
    endDate: '',
  });

  const { data, isLoading } = useQuery({
    queryKey: ['inventory-movements', page, pageSize, activeFilters.movementType, dateRange],
    queryFn: () =>
      fetchMovements(page, pageSize, {
        movementType: activeFilters.movementType,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      }),
    enabled: !!user?.organizationId,
  });

  const filteredData = useMemo(() => {
    if (!data?.data) return [];

    return data.data.filter((item: InventoryMovement) => {
      if (searchValue) {
        const search = searchValue.toLowerCase();
        const productName = item.product?.name || '';
        const productSku = item.product?.sku || '';
        const reason = item.reason || '';
        const notes = item.notes || '';
        const matchesSearch =
          productName.toLowerCase().includes(search) ||
          productSku.toLowerCase().includes(search) ||
          reason.toLowerCase().includes(search) ||
          notes.toLowerCase().includes(search);
        if (!matchesSearch) return false;
      }
      return true;
    });
  }, [data?.data, searchValue]);

  const handleFilterChange = (field: string, value: string) => {
    setActiveFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    setActiveFilters({});
    setDateRange({ startDate: '', endDate: '' });
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const colors = mode === 'dark' ? {
    bg: palette.background,
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    border: palette.border,
    borderLight: palette.borderLight,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.background,
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    border: palette.light.border,
    borderLight: palette.light.borderLight,
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  return (
    <Box sx={{ ...animations.fadeIn }}>
      {/* Industrial Header */}
      <Box sx={{ mb: 4, ...animations.slideDown }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography
            variant="overline"
            sx={{
              color: palette.primary,
              fontFamily: typography.fontFamily.mono,
              fontSize: typography.fontSize.xs,
              letterSpacing: typography.letterSpacing.widest,
              fontWeight: typography.fontWeight.bold,
            }}
          >
            DATA LOG SYSTEM
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <HistoryIcon sx={{ fontSize: 40, color: palette.primary }} />
          <Typography
            variant="h3"
            sx={{
              fontFamily: typography.fontFamily.display,
              fontWeight: typography.fontWeight.extrabold,
              color: colors.textPrimary,
              letterSpacing: '-0.02em',
              lineHeight: 1.1,
            }}
          >
            MOVEMENT HISTORY
          </Typography>
        </Box>
      </Box>

      {/* Filters Card */}
      <Card
        sx={{
          backgroundColor: colors.surface,
          border: `2px solid ${colors.border}`,
          borderRadius: theme.radius.sm,
          p: 2,
          mb: 3,
          ...animations.slideUp(0.1),
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Search Bar */}
          <TextField
            placeholder="SEARCH MOVEMENTS..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: colors.surfaceElevated,
                border: `1px solid ${colors.border}`,
                borderRadius: theme.radius.sm,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.sm,
                '& fieldset': { border: 'none' },
                '&:hover': {
                  borderColor: colors.borderLight,
                },
                '&.Mui-focused': {
                  borderColor: palette.primary,
                },
              },
              '& .MuiInputBase-input': {
                color: colors.textPrimary,
                fontWeight: typography.fontWeight.medium,
                textTransform: 'uppercase',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: colors.textMuted }} />
                </InputAdornment>
              ),
            }}
          />

          {/* Date Range Filters */}
          <Stack direction="row" spacing={2} alignItems="center">
            <CalendarTodayOutlinedIcon sx={{ color: colors.textMuted, fontSize: 20 }} />
            <Typography
              variant="body2"
              sx={{
                fontWeight: typography.fontWeight.semibold,
                color: colors.textPrimary,
                fontFamily: typography.fontFamily.mono,
                textTransform: 'uppercase',
                fontSize: typography.fontSize.xs,
              }}
            >
              Time Range
            </Typography>
            <TextField
              type="date"
              label="Start Date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange((prev) => ({ ...prev, startDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: colors.surfaceElevated,
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                },
              }}
            />
            <Typography variant="body2" sx={{ color: colors.textMuted }}>
              to
            </Typography>
            <TextField
              type="date"
              label="End Date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange((prev) => ({ ...prev, endDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: colors.surfaceElevated,
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                },
              }}
            />
            <Box sx={{ ml: 'auto' }}>
              <TableToolbar
                title=""
                searchValue=""
                onSearchChange={() => {}}
                searchPlaceholder=""
                filters={filterOptions}
                activeFilters={activeFilters}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                data={filteredData}
                columns={exportColumns}
                exportFileName="stock-movements"
              />
            </Box>
          </Stack>
        </Box>
      </Card>

      {/* Data Table - Industrial Control Room Style */}
      <Card
        sx={{
          backgroundColor: colors.surface,
          border: `2px solid ${colors.border}`,
          borderRadius: theme.radius.sm,
          overflow: 'hidden',
          ...animations.slideUp(0.2),
        }}
      >
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: palette.primary }} />
          </Box>
        ) : filteredData.length === 0 ? (
          <Box sx={{ p: 8, textAlign: 'center' }}>
            <HistoryIcon sx={{ fontSize: 80, color: colors.textMuted, mb: 2 }} />
            <Typography
              variant="h5"
              sx={{
                color: colors.textPrimary,
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.bold,
                mb: 1,
              }}
            >
              NO MOVEMENT RECORDS
            </Typography>
            <Typography variant="body2" sx={{ color: colors.textMuted }}>
              No movement history found for the selected criteria
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table
                sx={{
                  '& .MuiTableCell-root': {
                    borderBottom: `1px solid ${colors.border}`,
                  },
                }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: colors.surfaceElevated,
                      borderBottom: `2px solid ${palette.primary}`,
                    }}
                  >
                    <TableCell
                      sx={{
                        color: colors.textMuted,
                        fontFamily: typography.fontFamily.mono,
                        fontSize: typography.fontSize.xs,
                        fontWeight: typography.fontWeight.bold,
                        textTransform: 'uppercase',
                        letterSpacing: typography.letterSpacing.widest,
                        py: 2,
                      }}
                    >
                      Timestamp
                    </TableCell>
                    <TableCell
                      sx={{
                        color: colors.textMuted,
                        fontFamily: typography.fontFamily.mono,
                        fontSize: typography.fontSize.xs,
                        fontWeight: typography.fontWeight.bold,
                        textTransform: 'uppercase',
                        letterSpacing: typography.letterSpacing.widest,
                      }}
                    >
                      Product
                    </TableCell>
                    <TableCell
                      sx={{
                        color: colors.textMuted,
                        fontFamily: typography.fontFamily.mono,
                        fontSize: typography.fontSize.xs,
                        fontWeight: typography.fontWeight.bold,
                        textTransform: 'uppercase',
                        letterSpacing: typography.letterSpacing.widest,
                      }}
                    >
                      Operation
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        color: colors.textMuted,
                        fontFamily: typography.fontFamily.mono,
                        fontSize: typography.fontSize.xs,
                        fontWeight: typography.fontWeight.bold,
                        textTransform: 'uppercase',
                        letterSpacing: typography.letterSpacing.widest,
                      }}
                    >
                      Delta
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        color: colors.textMuted,
                        fontFamily: typography.fontFamily.mono,
                        fontSize: typography.fontSize.xs,
                        fontWeight: typography.fontWeight.bold,
                        textTransform: 'uppercase',
                        letterSpacing: typography.letterSpacing.widest,
                      }}
                    >
                      Before
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        color: colors.textMuted,
                        fontFamily: typography.fontFamily.mono,
                        fontSize: typography.fontSize.xs,
                        fontWeight: typography.fontWeight.bold,
                        textTransform: 'uppercase',
                        letterSpacing: typography.letterSpacing.widest,
                      }}
                    >
                      After
                    </TableCell>
                    <TableCell
                      sx={{
                        color: colors.textMuted,
                        fontFamily: typography.fontFamily.mono,
                        fontSize: typography.fontSize.xs,
                        fontWeight: typography.fontWeight.bold,
                        textTransform: 'uppercase',
                        letterSpacing: typography.letterSpacing.widest,
                      }}
                    >
                      Reason / Notes
                    </TableCell>
                    <TableCell
                      sx={{
                        color: colors.textMuted,
                        fontFamily: typography.fontFamily.mono,
                        fontSize: typography.fontSize.xs,
                        fontWeight: typography.fontWeight.bold,
                        textTransform: 'uppercase',
                        letterSpacing: typography.letterSpacing.widest,
                      }}
                    >
                      User
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData.map((movement: InventoryMovement, index: number) => {
                    const movementColor = getMovementTypeColor(movement.movementType);
                    const isPositive = movement.quantityChange > 0;
                    const isNegative = movement.quantityChange < 0;

                    return (
                      <TableRow
                        key={movement.id}
                        sx={{
                          transition: theme.transitions.fast,
                          borderLeft: `4px solid ${movementColor}`,
                          ...animations.tableRowFade(index),
                          '&:hover': {
                            backgroundColor: alpha(movementColor, 0.05),
                            borderLeft: `4px solid ${movementColor}`,
                          },
                        }}
                      >
                        {/* Timestamp */}
                        <TableCell sx={{ py: 2 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: typography.fontFamily.mono,
                              fontSize: typography.fontSize.xs,
                              color: colors.textSecondary,
                              lineHeight: 1.4,
                            }}
                          >
                            {new Date(movement.createdAt).toLocaleDateString('en-US', {
                              month: 'short',
                              day: '2-digit',
                              year: 'numeric',
                            })}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: typography.fontFamily.mono,
                              fontSize: typography.fontSize.xs,
                              color: colors.textMuted,
                            }}
                          >
                            {new Date(movement.createdAt).toLocaleTimeString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </Typography>
                        </TableCell>

                        {/* Product */}
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: typography.fontFamily.display,
                              fontWeight: typography.fontWeight.semibold,
                              color: colors.textPrimary,
                              mb: 0.5,
                            }}
                          >
                            {movement.product?.name || 'Unknown'}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: typography.fontFamily.mono,
                              fontSize: typography.fontSize.xs,
                              color: colors.textMuted,
                              letterSpacing: typography.letterSpacing.wide,
                            }}
                          >
                            SKU: {movement.product?.sku || '-'}
                          </Typography>
                        </TableCell>

                        {/* Operation Type */}
                        <TableCell>
                          <Chip
                            label={movementTypeLabels[movement.movementType]}
                            sx={{
                              backgroundColor: alpha(movementColor, 0.1),
                              color: movementColor,
                              fontFamily: typography.fontFamily.mono,
                              fontSize: typography.fontSize.xs,
                              fontWeight: typography.fontWeight.bold,
                              border: `1px solid ${movementColor}`,
                              height: 28,
                              ...animations.hoverLift,
                            }}
                          />
                        </TableCell>

                        {/* Delta (Change) */}
                        <TableCell align="center">
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              px: 1.5,
                              py: 0.5,
                              borderRadius: theme.radius.xs,
                              backgroundColor: isPositive
                                ? alpha(palette.success, 0.1)
                                : isNegative
                                ? alpha(palette.error, 0.1)
                                : 'transparent',
                              border: `1px solid ${
                                isPositive
                                  ? palette.success
                                  : isNegative
                                  ? palette.error
                                  : colors.border
                              }`,
                            }}
                          >
                            {isPositive ? (
                              <TrendingUpIcon sx={{ fontSize: 16, color: palette.success }} />
                            ) : isNegative ? (
                              <TrendingDownIcon sx={{ fontSize: 16, color: palette.error }} />
                            ) : null}
                            <Typography
                              sx={{
                                fontFamily: typography.fontFamily.mono,
                                fontSize: typography.fontSize.sm,
                                fontWeight: typography.fontWeight.bold,
                                color: isPositive
                                  ? palette.success
                                  : isNegative
                                  ? palette.error
                                  : colors.textPrimary,
                              }}
                            >
                              {isPositive ? `+${movement.quantityChange}` : movement.quantityChange}
                            </Typography>
                          </Box>
                        </TableCell>

                        {/* Before */}
                        <TableCell align="center">
                          <Typography
                            sx={{
                              fontFamily: typography.fontFamily.mono,
                              fontSize: typography.fontSize.sm,
                              fontWeight: typography.fontWeight.bold,
                              color: colors.textPrimary,
                            }}
                          >
                            {movement.quantityBefore}
                          </Typography>
                        </TableCell>

                        {/* After */}
                        <TableCell align="center">
                          <Typography
                            sx={{
                              fontFamily: typography.fontFamily.mono,
                              fontSize: typography.fontSize.sm,
                              fontWeight: typography.fontWeight.bold,
                              color: palette.primary,
                            }}
                          >
                            {movement.quantityAfter}
                          </Typography>
                        </TableCell>

                        {/* Reason/Notes */}
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: typography.fontFamily.body,
                              fontSize: typography.fontSize.sm,
                              color: colors.textSecondary,
                              maxWidth: 250,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {movement.reason || movement.notes || '-'}
                          </Typography>
                        </TableCell>

                        {/* User */}
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: typography.fontFamily.mono,
                              fontSize: typography.fontSize.xs,
                              color: colors.textSecondary,
                            }}
                          >
                            {movement.performedBy
                              ? `${movement.performedBy.firstName || ''} ${movement.performedBy.lastName || ''}`.trim() ||
                                movement.performedBy.email
                              : 'SYSTEM'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Industrial Pagination */}
            <Box
              sx={{
                backgroundColor: colors.surfaceElevated,
                borderTop: `2px solid ${colors.border}`,
              }}
            >
              <TablePagination
                component="div"
                count={data?.total || 0}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={pageSize}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{
                  '& .MuiTablePagination-toolbar': {
                    color: colors.textPrimary,
                    fontFamily: typography.fontFamily.mono,
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.xs,
                    fontWeight: typography.fontWeight.bold,
                    textTransform: 'uppercase',
                    letterSpacing: typography.letterSpacing.wide,
                  },
                  '& .MuiSelect-select': {
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.sm,
                    fontWeight: typography.fontWeight.bold,
                  },
                }}
              />
            </Box>
          </>
        )}
      </Card>
    </Box>
  );
};
